import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createImmunizationSchema,
  updateImmunizationSchema,
  type CreateImmunizationInput,
} from "@shared/validation/immunization.schema";
import {
  useCreateImmunization,
  useUpdateImmunization,
  type ImmunizationSummary,
} from "../../lib/api/useImmunizations";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { DialogShell } from "../ui/DialogShell";
import { Field } from "../ui/Field";
import { Button } from "../ui/Button";

// Empty date/text inputs submit "" — coerce to null so the schema (nullable)
// accepts them instead of failing the YYYY-MM-DD regex on an empty string.
const nullifyEmpty = {
  setValueAs: (v: unknown): string | null =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : null,
};

interface ImmunizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  immunization?: ImmunizationSummary;
}

export function ImmunizationDialog({
  open,
  onOpenChange,
  childId,
  immunization,
}: ImmunizationDialogProps) {
  const isEdit = Boolean(immunization);
  const createMutation = useCreateImmunization(childId);
  const updateMutation = useUpdateImmunization(childId, immunization?.id ?? "");
  const activeMutation = isEdit ? updateMutation : createMutation;
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateImmunizationInput>({
    resolver: zodResolver(isEdit ? updateImmunizationSchema : createImmunizationSchema),
    defaultValues: {
      vaccineName: immunization?.vaccineName ?? "",
      dateAdministered: immunization?.dateAdministered ?? null,
      nextDueDate: immunization?.nextDueDate ?? null,
      provider: immunization?.provider ?? null,
      notes: immunization?.notes ?? null,
    },
  });

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset({
        vaccineName: immunization?.vaccineName ?? "",
        dateAdministered: immunization?.dateAdministered ?? null,
        nextDueDate: immunization?.nextDueDate ?? null,
        provider: immunization?.provider ?? null,
        notes: immunization?.notes ?? null,
      });
      createMutation.reset();
      updateMutation.reset();
    }
    wasOpen.current = open;
  }, [open, immunization, reset, createMutation, updateMutation]);

  const onSubmit = async (data: CreateImmunizationInput) => {
    try {
      await activeMutation.mutateAsync(data);
      publish({
        variant: "success",
        title: isEdit ? "Immunization updated successfully" : "Immunization added successfully",
      });
      onOpenChange(false);
    } catch (err) {
      publish({
        variant: "error",
        title: isEdit ? "Failed to update immunization" : "Failed to add immunization",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={isEdit ? "Edit Immunization" : "Add Immunization"}
      description={
        isEdit
          ? "Update this immunization record."
          : "Record a vaccine — the date given and, if relevant, when the next dose is due."
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <Field label="Vaccine *" htmlFor="imm-name" error={errors.vaccineName?.message}>
          <input
            id="imm-name"
            type="text"
            {...register("vaccineName")}
            className={INPUT_CLASS}
            placeholder="e.g., MMR, DTaP, Flu"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Date Given" htmlFor="imm-date" error={errors.dateAdministered?.message}>
            <input
              id="imm-date"
              type="date"
              {...register("dateAdministered", nullifyEmpty)}
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Next Dose Due" htmlFor="imm-due" error={errors.nextDueDate?.message}>
            <input
              id="imm-due"
              type="date"
              {...register("nextDueDate", nullifyEmpty)}
              className={INPUT_CLASS}
            />
          </Field>
        </div>

        <Field label="Provider" htmlFor="imm-provider">
          <input
            id="imm-provider"
            type="text"
            {...register("provider", nullifyEmpty)}
            className={INPUT_CLASS}
            placeholder="Clinic or doctor who administered it"
          />
        </Field>

        <Field label="Notes" htmlFor="imm-notes">
          <textarea
            id="imm-notes"
            rows={2}
            {...register("notes", nullifyEmpty)}
            className={INPUT_CLASS}
          />
        </Field>

        {activeMutation.error instanceof ApiError && (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {activeMutation.error.message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || activeMutation.isPending}>
            {activeMutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Add Immunization"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
